import { CSSProperties } from 'react';
import { EVENT_Z_INDICES } from '../../constants';
import { useCalendarGridStore } from '../../store/calendar-grid.store';
import { CalendarEvent, CalendarEventType } from '../../types';
import { EventShell } from './event-shell';
import { AppointmentEvent } from './variants/appointment-event';
import { AvailabilitySlotEvent } from './variants/availability-slot-event';
import { AvailableBlockEvent } from './variants/available-block-event';
import { CancelledAppointmentEvent } from './variants/cancelled-appointment-event';
import { ExceptionEvent } from './variants/exception-event';
import { NonInteractiveSlotEvent } from './variants/non-interactive-slot';
import { ScheduleDefinitionEvent } from './variants/schedule-definition-event';
import { ScheduleRequestEvent } from './variants/schedule-request';
import { SlotStartLabelEvent } from './variants/slot-start-label';
import { UnavailableBlockEvent } from './variants/unavailable-block-event';

const EVENT_CSS_MAP: Record<CalendarEventType, CSSProperties> = {
  appointment: { zIndex: EVENT_Z_INDICES.appointment, margin: '1px 2px 1px 6px' },
  scheduleDefinition: { zIndex: EVENT_Z_INDICES.scheduleDefinition, margin: '0 0 1px 1px' },
  availabilitySlot: { zIndex: EVENT_Z_INDICES.availabilitySlot, margin: '0 0 1px 0' },
  availableBlock: { zIndex: EVENT_Z_INDICES.availableBlock },
  cancelledAppointment: { zIndex: EVENT_Z_INDICES.cancelledAppointment, margin: '1px 2px 1px 6px' },
  exception: { zIndex: EVENT_Z_INDICES.exception },
  scheduleRequest: { zIndex: EVENT_Z_INDICES.scheduleRequest, margin: '1px 2px 1px 6px' },
  unavailableBlock: { zIndex: EVENT_Z_INDICES.unavailableBlock },
  nonInteractiveSlot: { zIndex: EVENT_Z_INDICES.nonInteractiveSlot },
  slotsStartLabel: { zIndex: EVENT_Z_INDICES.slotsStartLabel },
};

const renderEvent = (eventInfo: CalendarEvent) => {
  switch (eventInfo.eventType) {
    case 'appointment':
      return <AppointmentEvent event={eventInfo} />;
    case 'scheduleDefinition':
      return <ScheduleDefinitionEvent event={eventInfo} />;
    case 'availabilitySlot':
      return <AvailabilitySlotEvent event={eventInfo} />;
    case 'availableBlock':
      return <AvailableBlockEvent event={eventInfo} />;
    case 'cancelledAppointment':
      return <CancelledAppointmentEvent event={eventInfo} />;
    case 'exception':
      return <ExceptionEvent event={eventInfo} />;
    case 'scheduleRequest':
      return <ScheduleRequestEvent event={eventInfo} />;
    case 'unavailableBlock':
      return <UnavailableBlockEvent event={eventInfo} />;
    case 'nonInteractiveSlot':
      return <NonInteractiveSlotEvent event={eventInfo} />;
    case 'slotsStartLabel':
      return <SlotStartLabelEvent event={eventInfo} />;
    default:
      return null;
  }
};

interface Props {
  eventId: string;
}

export const EventRenderer = ({ eventId }: Props) => {
  const eventInfo = useCalendarGridStore((s) => s.eventsById[eventId]);
  if (!eventInfo) return null;

  const style = EVENT_CSS_MAP[eventInfo.eventType] || {};
  const content = renderEvent(eventInfo);
  if (!content) return null;

  return (
    <EventShell id={eventInfo.id} startTime={eventInfo.startTime} duration={eventInfo.duration} style={style}>
      {content}
    </EventShell>
  );
};
